import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthStore } from '@auth/store/auth.store';
import { waitUntilPredicate } from '@auth/utils/wait-until-predicate.utils';

/**
 * Guard that requires the user to have ANY of the specified roles
 */
export const requireAnyRole = (...roles: string[]): CanActivateFn => {
  return async (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    try {
      await waitUntilPredicate(() => !authStore.isLoading());
    } catch {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (!authStore.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (authStore.hasAnyRole(roles)) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};

/**
 * Guard that requires the user to have ALL of the specified roles
 */
export const requireAllRoles = (...roles: string[]): CanActivateFn => {
  return async (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    try {
      await waitUntilPredicate(() => !authStore.isLoading());
    } catch {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (!authStore.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (authStore.hasAllRoles(roles)) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};

/**
 * Guard that requires the user to have ONLY the specified roles (no more, no less)
 */
export const requireOnlyRoles = (...roles: string[]): CanActivateFn => {
  return async (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    try {
      await waitUntilPredicate(() => !authStore.isLoading());
    } catch {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (!authStore.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    if (authStore.hasOnlyRoles(roles)) {
      return true;
    }

    return router.createUrlTree(['/unauthorized']);
  };
};
